/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {map, startWith} from 'rxjs/operators';
import {ALL, DISMISS_MSG} from '../../../../common/constants';
import {Dropdown} from '../../../../models/engine-attribute-dropdown.model';
import {EngineAttributesService} from '../../../../services/engine-attributes.service';
import {ExcelExportService} from '../../../../services/excel-export.service';
import {UpdateAttributePopupComponent} from '../update-attribute-popup/update-attribute-popup.component';


@Component({
    selector: 'app-engine-attributes',
    templateUrl: './engine-attributes.component.html',
    styleUrls: ['./engine-attributes.component.scss'],
    standalone: false
})
export class EngineAttributesComponent implements OnInit {
  showSpinner: boolean;
  ALL = ALL;

  engineFamily: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGFAM',
    model: new FormControl()
  };

  engineRPO: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGRPO',
    model: new FormControl()
  };

  batRPO: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'BATTERY',
    model: new FormControl()
  };

  boost: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'BOOST',
    model: new FormControl()
  };

  engineCam: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGCAM',
    model: new FormControl()
  };

  engineDSP: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGDSP',
    model: new FormControl()
  };

  engineCYL: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGCYL',
    model: new FormControl()
  };

  enginePower: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGPWR',
    model: new FormControl()
  };

  engineTorque: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'ENGTORQ',
    model: new FormControl()
  };

  vFuel: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'VFUEL',
    model: new FormControl()
  };

  injection: Dropdown = {
    filteredOptions: null,
    availableOptions: [],
    selectedOptions: '%',
    searchAttribute: 'INJECTION',
    model: new FormControl()
  };

  dropdownArray: Dropdown[] = [this.injection, this.vFuel, this.engineTorque, this.enginePower, this.engineCYL,
    this.engineDSP, this.engineCam, this.boost, this.batRPO,
    this.engineRPO, this.engineFamily];

  constructor(private readonly engineAttributesService: EngineAttributesService,
              public dialog: MatDialog,
              private readonly excelExport: ExcelExportService,
              public snackBar: FabMessageService) {
  }

  ngOnInit(): void {
    this.showSpinner = true;
    this.getFilterOptions();
  }

  /**
   * Get filter option.
   * @param attribute is a column name used to retrieve data from DB.
   * @param dropdown will be assigned value retrieved from DB.
   */
  getData(attribute: string, dropdown: Dropdown): void {
    this.engineAttributesService.getFilterOptions(attribute, this.engineFamily.selectedOptions,
      this.engineRPO.selectedOptions, this.batRPO.selectedOptions, this.boost.selectedOptions,
      this.engineCam.selectedOptions, this.engineDSP.selectedOptions, this.engineCYL.selectedOptions,
      this.enginePower.selectedOptions, this.engineTorque.selectedOptions, this.vFuel.selectedOptions,
      this.injection.selectedOptions).subscribe(data => {
        dropdown.availableOptions = data.map(a => a[dropdown.searchAttribute]);
        dropdown.availableOptions.unshift(ALL);
        dropdown.model.setValue(ALL);
        this.updateFilteredDropdownOptions(dropdown);
        this.updateSelectedValue(dropdown);

        if (dropdown.searchAttribute === 'ENGFAM') {
          this.showSpinner = false;
        }
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Generate report of all data that will be updated.
   * @param attribute is a column name used to retrieve data from DB.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param newValue will replace 'currentValue'.
   */
  previewChanges(attribute: string, currentValue: string, newValue: string): void {
    this.engineAttributesService.getPreviewChanges(attribute, currentValue, newValue, this.engineFamily.selectedOptions,
      this.engineRPO.selectedOptions, this.batRPO.selectedOptions, this.boost.selectedOptions,
      this.engineCam.selectedOptions, this.engineDSP.selectedOptions, this.engineCYL.selectedOptions,
      this.enginePower.selectedOptions, this.engineTorque.selectedOptions, this.vFuel.selectedOptions,
      this.injection.selectedOptions).subscribe(
      response => {
        this.showSpinner = false;
        const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
        this.excelExport.exportToExcelFromServer(response, 'ChangeAttributeEng_' + currDate);
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Update an attribute value, and generate before and after changes report.
   * @param attribute is a column name used to retrieve data from DB.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param newValue will replace 'currentValue'.
   */
  commitChanges(attribute: string, currentValue: string, newValue: string): void {
    this.engineAttributesService.getCommitChanges(attribute, currentValue, newValue, this.engineFamily.selectedOptions,
      this.engineRPO.selectedOptions, this.batRPO.selectedOptions, this.boost.selectedOptions,
      this.engineCam.selectedOptions, this.engineDSP.selectedOptions, this.engineCYL.selectedOptions,
      this.enginePower.selectedOptions, this.engineTorque.selectedOptions, this.vFuel.selectedOptions,
      this.injection.selectedOptions).subscribe(
      response => {
        const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
        this.excelExport.exportToExcelFromServer(response, 'ChangeAttributeEng_' + currDate);
      },
      (error) => {
        this.showSpinner = false;
        this.snackBar.displayError(error, DISMISS_MSG);
      }, () => {
        this.resetFilter();
        this.showSpinner = false;
      });
  }


  /**
   * Open a popup to preview/commit changes.
   * @param title of the Filter option that will be updated.
   * @param currentValue is an existing value in DB that need to be updated.
   * @param attribute is a column name used to retrieve data from DB.
   */
  openDialog(title: string, currentValue: string, attribute: string): void {
    const dialogRef = this.dialog.open(UpdateAttributePopupComponent, {
      data: {
        header: title,
        selectedValue: currentValue,
        attributeToUpdate: attribute,
        maxLength: 255
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.showSpinner = true;
        if (res.data.isPreviewChange) {
          this.previewChanges(attribute, currentValue, res.data.newValue);
        } else {
          this.commitChanges(attribute, currentValue, res.data.newValue);
        }
      }
    });
  }

  /**
   * Get default filter options.
   */
  getFilterOptions(): void {
    for (const dropdown of this.dropdownArray) {
      this.getData(dropdown.searchAttribute, dropdown);
    }
  }

  /**
   * On Value Change filter data.
   * @param dropdown current filter option.
   */
  updateFilteredDropdownOptions(dropdown: Dropdown): void {
    dropdown.filteredOptions = dropdown.model.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(dropdown.availableOptions, value || ''))
    );
  }

  /**
   * If dropdown value is All, then set text field value to empty on click.
   * @param menu selected dropdown
   */
  resetText(menu: Dropdown): void {
    if (menu.model.value === ALL) {
      menu.model.setValue('');
    }
  }

  /**
   * Filters option by search string; show all options when searching "All".
   * @param options, all available options.
   * @param value users input.
   */
  _filter(options: string[], value: string): string[] {
    const filterValue = value.toLowerCase();
    return options.filter(option => filterValue === ALL.toLowerCase()
      ? option.toLowerCase().includes('') : option.toLowerCase().includes(filterValue));
  }

  /**
   * Reset all filter options.
   */
  resetFilter(): void {
    this.showSpinner = true;
    this.resetSelectionAndBelow('');
    this.getFilterOptions();
  }

  /**
   * Reset selection of all the remaining filters
   * @param searchAttribute used to identify with filter was selected.
   */
  resetSelectionAndBelow(searchAttribute): void {
    const DropdownArray: Dropdown[] = [this.injection, this.vFuel, this.engineTorque, this.enginePower, this.engineCYL,
      this.engineDSP, this.engineCam, this.boost, this.batRPO,
      this.engineRPO, this.engineFamily];
    for (const dropdown of DropdownArray) {
      if (searchAttribute.toLowerCase() === dropdown.searchAttribute.toLowerCase()) {
        break;
      }
      if (searchAttribute.toLowerCase() !== dropdown.searchAttribute) {
        dropdown.model.setValue(ALL);
        dropdown.selectedOptions = '%';
      }
    }
  }

  /**
   * Replace 'All' with '%'.
   * @param dropdown Current selected filter option.
   */
  replaceAllValue(dropdown: Dropdown): void {
    if (dropdown.selectedOptions === ALL) {
      dropdown.selectedOptions = '%';
    }
  }

  /**
   * Set the value for Engine Family Dropdown.
   * Get filter option for Engine RPO.
   */
  onChangeEngineFamily(): void {
    this.resetSelectionAndBelow(this.engineFamily.searchAttribute);
    this.engineFamily.selectedOptions = this.engineFamily.model.value;
    this.replaceAllValue(this.engineFamily);
    this.getData(this.engineRPO.searchAttribute, this.engineRPO);
    this.onChangeEngineRPO();
  }

  /**
   * Set the value for Engine RPO Dropdown.
   * Get filter option for Bat RPO.
   */
  onChangeEngineRPO(): void {
    this.resetSelectionAndBelow(this.engineRPO.searchAttribute);
    this.engineRPO.selectedOptions = this.engineRPO.model.value;
    this.replaceAllValue(this.engineRPO);
    this.getData(this.batRPO.searchAttribute, this.batRPO);
    this.onChangeBatRpo();
  }

  /**
   * Set the value for Bat RPO Dropdown.
   * Get filter option for Boost.
   */
  onChangeBatRpo(): void {
    this.resetSelectionAndBelow(this.batRPO.searchAttribute);
    this.batRPO.selectedOptions = this.batRPO.model.value;
    this.replaceAllValue(this.batRPO);
    this.getData(this.boost.searchAttribute, this.boost);
    this.onChangeBoost();
  }

  /**
   * Set the value for Boost Dropdown.
   * Get filter option for Engine Cam.
   */
  onChangeBoost(): void {
    this.resetSelectionAndBelow(this.boost.searchAttribute);
    this.boost.selectedOptions = this.boost.model.value;
    this.replaceAllValue(this.boost);
    this.getData(this.engineCam.searchAttribute, this.engineCam);
    this.onChangeEngineCam();
  }

  /**
   * Set the value for Engine Cam Dropdown.
   * Get filter option for Engine DSP
   */
  onChangeEngineCam(): void {
    this.resetSelectionAndBelow(this.engineCam.searchAttribute);
    this.engineCam.selectedOptions = this.engineCam.model.value;
    this.replaceAllValue(this.engineCam);
    this.getData(this.engineDSP.searchAttribute, this.engineDSP);
    this.onChangeEngineDSP();
  }

  /**
   * Set the value for Engine DSP Dropdown.
   * Get filter option for Engine CYL.
   */
  onChangeEngineDSP(): void {
    this.resetSelectionAndBelow(this.engineDSP.searchAttribute);
    this.engineDSP.selectedOptions = this.engineDSP.model.value;
    this.replaceAllValue(this.engineDSP);
    this.getData(this.engineCYL.searchAttribute, this.engineCYL);
    this.onChangeEngineCYL();
  }

  /**
   * Set the value for Engine CYL Dropdown.
   * Get filter option for Engine Power.
   */
  onChangeEngineCYL(): void {
    this.resetSelectionAndBelow(this.engineCYL.searchAttribute);
    this.engineCYL.selectedOptions = this.engineCYL.model.value;
    this.replaceAllValue(this.engineCYL);
    this.getData(this.enginePower.searchAttribute, this.enginePower);
    this.onChangeEnginePower();
  }

  /**
   * Set the value for Engine Power Dropdown.
   * Get filter options for Engine Torque.
   */
  onChangeEnginePower(): void {
    this.resetSelectionAndBelow(this.enginePower.searchAttribute);
    this.enginePower.selectedOptions = this.enginePower.model.value;
    this.replaceAllValue(this.enginePower);
    this.getData(this.engineTorque.searchAttribute, this.engineTorque);
    this.onChangeEngineTorque();
  }

  /**
   * Set the value for Engine Torque Dropdown.
   * Get filter options for VFuel.
   */
  onChangeEngineTorque(): void {
    this.resetSelectionAndBelow(this.engineTorque.searchAttribute);
    this.engineTorque.selectedOptions = this.engineTorque.model.value;
    this.replaceAllValue(this.engineTorque);
    this.getData(this.vFuel.searchAttribute, this.vFuel);
    this.onChangeVFuel();
  }

  /**
   * Set the value for VFuel Dropdown.
   */
  onChangeVFuel(): void {
    this.resetSelectionAndBelow(this.vFuel.searchAttribute);
    this.vFuel.selectedOptions = this.vFuel.model.value;
    this.replaceAllValue(this.vFuel);
    this.getData(this.injection.searchAttribute, this.injection);
  }

  /**
   * Set the value for Injection Dropdown.
   */
  onChangeInjection(): void {
    this.injection.selectedOptions = this.injection.model.value;
    this.replaceAllValue(this.vFuel);
  }

  /**
   * If available options only have 1 value except 'All', select the only value.
   * @param dropdown selected Dropdown option
   */
  updateSelectedValue(dropdown: Dropdown): void {
    if (dropdown.availableOptions.length === 2) {
      dropdown.model.setValue(dropdown.availableOptions[1]);
      dropdown.selectedOptions = dropdown.availableOptions[1];
    }
  }
}
